import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ContentDetector from './ContentDetector';
import PlatformIcon from './PlatformIcon';
import { formatTime, getAlertType, getSeverity, normalizePlatform } from './Formatting';
import { getBackend, getJWTFromLocalStorage } from '../services/helpers';

// ── Live System Clock (Vancouver / Pacific time) ─────────────────────────────
const VAN_TZ = 'America/Vancouver';
const SystemClock = () => {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const timeStr = time.toLocaleTimeString('en-US', { timeZone: VAN_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  const tzLabel = time.toLocaleTimeString('en-US', { timeZone: VAN_TZ, timeZoneName: 'short' }).split(' ').pop();
  const dateStr = time.toLocaleDateString('en-US', { timeZone: VAN_TZ, weekday: 'short', month: 'short', day: '2-digit' }).toUpperCase();
  return (
    <div className="cyber-clock">
      <div>{timeStr} <span style={{ fontSize: '0.65em', opacity: 0.65 }}>{tzLabel}</span></div>
      <div className="clock-date">{dateStr}</div>
    </div>
  );
};

// ── Threat Level Gauge ────────────────────────────────────────────────────────
const ThreatGauge = ({ highCount, total }) => {
  const pct = total === 0 ? 0 : Math.min(100, Math.round((highCount / total) * 100));

  const getLevel = () => {
    if (pct === 0) return { label: 'MINIMAL', color: 'var(--threat-low)', glow: 'var(--glow-green)', fill: '#10b981' };
    if (pct < 20)  return { label: 'LOW',     color: 'var(--threat-low)', glow: 'var(--glow-green)', fill: '#10b981' };
    if (pct < 50)  return { label: 'ELEVATED', color: 'var(--threat-medium)', glow: 'var(--glow-amber)', fill: '#eab308' };
    if (pct < 75)  return { label: 'HIGH',    color: 'var(--threat-critical)', glow: 'var(--glow-red)', fill: '#ef4444' };
    return { label: 'CRITICAL', color: 'var(--threat-critical)', glow: 'var(--glow-red)', fill: '#ef4444' };
  };

  const lvl = getLevel();

  return (
    <div className="threat-gauge-wrap">
      <div className="threat-gauge-level" style={{ color: lvl.color, textShadow: lvl.glow.replace('var(--glow-', '0 0 12px rgba(').replace(')', ', 0.8)') }}>
        <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', background: lvl.fill, boxShadow: `0 0 8px ${lvl.fill}`, animation: pct > 20 ? 'pulse-live 1.8s ease-in-out infinite' : 'none' }} />
        {lvl.label}
      </div>
      <div className="threat-gauge-track">
        <div className="threat-gauge-fill" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${lvl.fill}99, ${lvl.fill})`, boxShadow: `0 0 10px ${lvl.fill}55` }} />
      </div>
      <div className="threat-metrics">
        {[
          { label: 'Critical / Total', value: `${highCount} / ${total}`, color: highCount > 0 ? 'var(--threat-critical)' : 'var(--threat-low)' },
          { label: 'Threat Score', value: `${pct}%`, color: lvl.color },
          { label: 'AI Analysis', value: 'ACTIVE', color: 'var(--cyan-400)' },
        ].map((m, i) => (
          <div key={i} className="threat-metric-row">
            <span className="threat-metric-label">{m.label}</span>
            <span className="threat-metric-value" style={{ color: m.color, textShadow: `0 0 8px ${m.color}55` }}>{m.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Content label for dashboard (no actual media rendered) ────────────────────
const getContentLabel = (raw) => {
  if (!raw) return 'No content';
  try {
    const p = JSON.parse(raw);
    if (p.filepath) {
      const folder = p.filepath.split('/')[0];
      if (folder === 'voice') return '[ VOICE RECORDING ]';
      if (folder === 'meme') return '[ MEME IMAGE ]';
      return '[ IMAGE CONTENT ]';
    }
    return p.content || 'No content';
  } catch {
    return raw;
  }
};

// ── Inline Alert Card ─────────────────────────────────────────────────────────
const AlertCard = ({ alert, onCheck, onDismiss, wards = [] }) => {
  const isApiAlert = alert.hasOwnProperty('content_type');

  const resolveName = (handle) => {
    if (!handle) return 'Unknown';
    const id = parseInt(handle);
    if (!isNaN(id) && id !== 0) {
      const w = wards.find(w => w.id === id);
      if (w) return w.full_name || w.username;
    }
    return handle;
  };

  const alertData = isApiAlert
    ? {
        id: alert.id,
        type: getAlertType(alert.content_type),
        message: getContentLabel(alert.content),
        platform: alert.source_platform || 'Unknown',
        from: resolveName(alert.sender_handle),
        time: formatTime(alert.timestamp),
        severity: getSeverity(alert.classification),
      }
    : alert;

  const ward = wards.find(w => w.id === alert.recipient_id);
  const wardName = ward ? (ward.full_name || ward.username) : null;

  const icons = { harmful: '!', 'not-harmful': 'i' };
  const sev = alertData.severity;

  return (
    <div className={`alert-item ${sev}`} style={{ animation: 'slideUp 0.3s ease' }}>
      <div className="alert-header">
        <div className="alert-header-left">
          <div className="alert-icon">{icons[sev] || 'i'}</div>
          <div className="alert-info">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
              <div className="alert-type">{alertData.type}</div>
              <span className={`severity-badge ${sev}`}>{sev?.toUpperCase().replace('-', ' ')}</span>
            </div>
            <div className="alert-message">"{alertData.message}"</div>
            <div className="alert-source" style={{ marginTop: '0.375rem' }}>
              <PlatformIcon platform={alertData.platform} />
              <span className="platform-name">{normalizePlatform(alertData.platform)}</span>
              <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.625rem' }}>·</span>
              <span className="sender-name" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{alertData.from}</span>
              {wardName && (
                <>
                  <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.625rem' }}>·</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--text-purple)' }}>{wardName}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.5625rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{alertData.time}</span>
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', paddingTop: '0.625rem', borderTop: '1px solid var(--border-dim)' }}>
        <button className="btn btn-ghost" style={{ fontSize: '0.6875rem', padding: '0.3rem 0.75rem' }} onClick={() => onDismiss(alertData.id)}>
          DISMISS
        </button>
        <button className="btn btn-cyber" style={{ fontSize: '0.6875rem', padding: '0.3rem 0.875rem' }} onClick={() => onCheck(alertData.id)}>
          INVESTIGATE →
        </button>
      </div>
    </div>
  );
};

const SERVER_URL = `http${process.env.REACT_APP_API_URL}`;
const DASHBOARD_LIMIT = 300;
const PAGE_SIZE = 100; // API maximum

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertCount, setAlertCount] = useState(0);
  const [highCount, setHighCount] = useState(0);
  const [error, setError] = useState(null);
  const [wards, setWards] = useState([]);

  const fallbackAlerts = [
    { id: 1, type: 'Blocked Harmful Message', message: 'Nobody likes you. Just leave already.', platform: 'Discord', from: 'Janet', time: 'Today, 2:32 PM', severity: 'harmful' },
    { id: 2, type: 'Potential Harmful Image', message: 'Snapchat image flagged for review', platform: 'Snapchat', from: 'Alex', time: 'Today, 8:25 AM', severity: 'not-harmful' },
  ];

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const token = await getJWTFromLocalStorage();
        if (!token) { navigate('/login'); return; }

        let all = [];
        let cursor = null;

        while (all.length < DASHBOARD_LIMIT) {
          const params = new URLSearchParams({ limit: PAGE_SIZE });
          if (cursor != null) params.append('before_id', cursor);

          const resp = await fetch(`${SERVER_URL}/incident-reports?${params}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (resp.status === 401) { navigate('/login'); return; }
          if (!resp.ok) throw new Error('Failed to fetch');

          const data = await resp.json();
          const page = Array.isArray(data) ? data : data.reports || data.items || data.data || data.incidents || [];

          all = [...all, ...page];
          if (page.length < PAGE_SIZE) break;
          cursor = Math.min(...page.map(r => r.id));
        }

        all = all.slice(0, DASHBOARD_LIMIT);
        setAlertCount(all.length);
        setHighCount(all.filter(r => getSeverity(r.classification) === 'harmful').length);
        setAlerts(all.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)));
      } catch {
        setError('API unavailable. Showing demo data.');
        setAlerts([]);
      }
    };

    const fetchWards = async () => {
      try {
        const resp = await getBackend('wards');
        const data = await resp.json();
        setWards(data || []);
      } catch { setWards([]); }
    };

    const run = async () => {
      setLoading(true);
      await Promise.all([fetchAlerts(), fetchWards()]);
      setLoading(false);
    };
    run();
  }, [navigate]);

  const handleCheck   = id => navigate(`/alerts?highlight=${id}`);
  const handleDismiss = id => {
    const ids = new Set(JSON.parse(localStorage.getItem('dismissedDashboardAlerts') || '[]'));
    ids.add(id);
    localStorage.setItem('dismissedDashboardAlerts', JSON.stringify([...ids]));
    setAlerts([...alerts]);
  };

  const getDashboardAlerts = () => {
    const ids = new Set(JSON.parse(localStorage.getItem('dismissedDashboardAlerts') || '[]'));
    return alerts.filter(a => !ids.has(a.id)).slice(0, 4);
  };

  const displayAlerts = alerts.length > 0 ? getDashboardAlerts() : (error ? fallbackAlerts : []);

  const userName = (() => {
    try { const u = JSON.parse(localStorage.getItem('user')); return u?.full_name?.split(' ')[0] || 'OPERATOR'; }
    catch { return 'OPERATOR'; }
  })();

  const wardColors = ['#7c3aed', '#06b6d4', '#8b5cf6', '#0891b2', '#a855f7'];

  const statCards = [
    { color: 'cyan',   icon: 'fa-database',            number: alertCount, label: 'Total Incidents',    link: 'VIEW LOG →',     onClick: () => navigate('/alerts') },
    { color: 'red',    icon: 'fa-triangle-exclamation', number: highCount,  label: 'Harmful Incidents', link: 'INVESTIGATE →',  onClick: () => navigate('/alerts'), alertActive: highCount > 0 },
    { color: 'green',  icon: 'fa-shield-halved',        number: wards.length, label: 'Protected Nodes',   link: 'MANAGE →',       onClick: () => navigate('/wards') },
  ];

  return (
    <div className="content-container">

      {/* ── Cyber Header ── */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-tag">// GUARDIAN SPHERE · THREAT INTELLIGENCE DASHBOARD</div>
          <h1 className="page-title" style={{ fontFamily: 'var(--font-mono)', letterSpacing: '0.02em', animation: 'glitch-flicker 8s ease-in-out infinite' }}>
            SECURITY OPERATIONS
          </h1>
          <p className="page-subtitle">
            Operator: <span style={{ color: 'var(--text-cyan)' }}>{userName}</span>
            {' · '}
            Monitoring <span style={{ color: 'var(--text-purple)' }}>{wards.length}</span> ward{wards.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.625rem' }}>
          <SystemClock />
          <div className={`status-badge ${highCount > 0 ? 'alert' : 'secure'}`}>
            <div className="live-dot" style={{ background: highCount > 0 ? 'var(--threat-critical)' : 'var(--threat-low)' }} />
            {highCount > 0 ? `${highCount} THREAT${highCount > 1 ? 'S' : ''} ACTIVE` : 'ALL SYSTEMS SECURE'}
          </div>
        </div>
      </div>

      {/* ── Metric Cards ── */}
      <div className="stat-grid">
        {statCards.map((s, i) => (
          <div
            key={i}
            className={`stat-card ${s.color}${s.alertActive ? ' alert-active' : ''}`}
            onClick={s.onClick}
            role="button" tabIndex={0}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 2 }}>
              <div className={`stat-card-icon ${s.color}`}>
                <i className={`fa-solid ${s.icon}`} />
              </div>
              {s.alertActive && (
                <div className="live-badge">
                  <div className="live-dot" style={{ background: 'var(--threat-critical)', boxShadow: 'var(--glow-red)' }} />
                  LIVE
                </div>
              )}
            </div>
            <div style={{ position: 'relative', zIndex: 2 }}>
              <div className="stat-number">{loading ? '—' : s.number}</div>
              <div className="stat-label">{s.label}</div>
            </div>
            <div className="stat-link" style={{ position: 'relative', zIndex: 2 }}>{s.link}</div>
          </div>
        ))}
      </div>

      {/* ── Main Grid ── */}
      <div className="dashboard-grid">

        {/* ── Live Incident Feed ── */}
        <div className="panel alerts-panel scan" style={{ gap: 0, minHeight: '24rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', position: 'relative', zIndex: 2 }}>
            <h3 className="panel-title" style={{ margin: 0 }}>LIVE INCIDENT FEED</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
              <div className="live-badge">
                <div className="live-dot" />
                LIVE
              </div>
              <button className="btn btn-cyber" style={{ fontSize: '0.625rem', padding: '0.25rem 0.625rem' }} onClick={() => navigate('/alerts')}>
                FULL LOG →
              </button>
            </div>
          </div>

          {loading ? (
            <div className="loading-indicator"><div className="loading-spinner" />SCANNING NETWORK…</div>
          ) : (
            <div className="alerts-list" style={{ position: 'relative', zIndex: 1 }}>
              {displayAlerts.length > 0 ? (
                displayAlerts.map(a => (
                  <AlertCard key={a.id} alert={a} onCheck={handleCheck} onDismiss={handleDismiss} wards={wards} />
                ))
              ) : (
                <div className="no-alerts-message">
                  <i className="fa-solid fa-circle-check" style={{ fontSize: '2.25rem', color: 'var(--threat-low)', filter: 'drop-shadow(0 0 8px rgba(16,185,129,0.6))' }} />
                  <p style={{ color: 'var(--threat-low)' }}>NO ACTIVE THREATS DETECTED</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', fontFamily: 'var(--font-mono)' }}>All endpoints nominal</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Right Column ── */}
        <div className="stats-container">

          {/* Threat Level */}
          <div className="panel" style={{ flex: '0 0 auto' }}>
            <h3 className="panel-title">THREAT LEVEL</h3>
            <ThreatGauge highCount={loading ? 0 : highCount} total={loading ? 0 : alertCount} />
          </div>

          {/* Endpoint Status */}
          <div className="panel" style={{ flex: 1 }}>
            <h3 className="panel-title">WARD STATUS</h3>
            {loading ? (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)' }}>LOADING…</div>
            ) : wards.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', fontFamily: 'var(--font-mono)', marginBottom: '0.75rem' }}>NO ENDPOINTS CONFIGURED</p>
                <button className="btn btn-cyber" style={{ fontSize: '0.625rem' }} onClick={() => navigate('/wards')}>
                  + ADD ENDPOINT
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                {wards.slice(0, 5).map((w, i) => {
                  const initials = (w.full_name || w.username || '?').split(' ').map(x => x[0]).join('').toUpperCase().slice(0, 2);
                  return (
                    <div key={w.id || i} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.375rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-dim)', background: 'rgba(4,6,14,0.4)' }}>
                      <div style={{ width: '2rem', height: '2rem', borderRadius: 'var(--radius-sm)', background: wardColors[i % wardColors.length], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6875rem', fontWeight: 800, color: 'white', flexShrink: 0, boxShadow: `0 0 8px ${wardColors[i % wardColors.length]}66` }}>
                        {initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {w.full_name || w.username}
                        </div>
                        {w.username && <div style={{ fontSize: '0.5625rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>@{w.username}</div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <div style={{ width: '0.4rem', height: '0.4rem', borderRadius: '50%', background: 'var(--threat-low)', boxShadow: 'var(--glow-green)', animation: 'pulse-live 2s ease-in-out infinite' }} />
                        <span style={{ fontSize: '0.5rem', fontFamily: 'var(--font-mono)', color: 'var(--threat-low)', letterSpacing: '0.06em' }}>ACTIVE</span>
                      </div>
                    </div>
                  );
                })}
                {wards.length > 5 && (
                  <button className="btn btn-ghost" style={{ fontSize: '0.6875rem', justifyContent: 'center', fontFamily: 'var(--font-mono)' }} onClick={() => navigate('/wards')}>
                    +{wards.length - 5} more endpoints
                  </button>
                )}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Content Analysis Terminal ── */}
      <ContentDetector />

    </div>
  );
};

export default Dashboard;
